import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Schedule } from "@/types";
import { getShiftTooltipData, getTooltipPosition, type TooltipState } from "@/utils/schedulerUtils";

function getEventPosition(e: React.MouseEvent | React.FocusEvent): { x: number; y: number } {
    if ('clientX' in e && e.detail > 0) {
        return { x: e.clientX, y: e.clientY };
    }
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top };
}

export const useShiftTooltip = (
    schedule: Schedule,
    shiftConstraintBreaches?: { [shiftId: string]: string[] }
) => {
    const [shiftTooltip, setShiftTooltip] = useState<TooltipState | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const isPinnedRef = useRef(false);
    const justClickedShiftRef = useRef(false);

    const handleShiftMouseEnter = useCallback((shiftId: string, e: React.MouseEvent | React.FocusEvent) => {
        if (isPinnedRef.current || !containerRef.current) {
            return;
        }

        const tooltipData = getShiftTooltipData(shiftId, schedule, shiftConstraintBreaches);
        if (!tooltipData) {
            return;
        }

        const containerRect = containerRef.current.getBoundingClientRect();
        const { x, y } = getEventPosition(e);
        const position = getTooltipPosition(x, y, containerRect);

        setShiftTooltip({ ...position, ...tooltipData, shiftId, isPinned: false });
    }, [schedule, shiftConstraintBreaches]);

    const handleGenericMouseEnter = useCallback((
        data: { shiftId: string; lines: string[]; timeRange: string; employeeName: string; roleName: string },
        e: React.MouseEvent | React.FocusEvent
    ) => {
        if (isPinnedRef.current || !containerRef.current) {
            return;
        }

        const containerRect = containerRef.current.getBoundingClientRect();
        const { x, y } = getEventPosition(e);
        const position = getTooltipPosition(x, y, containerRect);

        setShiftTooltip({ ...position, ...data, isPinned: false });
    }, []);

    const handleShiftMouseLeave = useCallback(() => {
        if (!isPinnedRef.current) {
            setShiftTooltip(null);
        }
    }, []);

    const handleShiftClick = useCallback((shiftId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        justClickedShiftRef.current = true;

        if (shiftTooltip?.shiftId === shiftId && isPinnedRef.current) {
            setShiftTooltip(null);
            isPinnedRef.current = false;
            return;
        }

        if (!containerRef.current) {
            return;
        }

        const tooltipData = getShiftTooltipData(shiftId, schedule, shiftConstraintBreaches);
        if (!tooltipData) {
            return;
        }

        const containerRect = containerRef.current.getBoundingClientRect();
        const { x, y } = getEventPosition(e);
        const position = getTooltipPosition(x, y, containerRect);

        setShiftTooltip({ ...position, ...tooltipData, shiftId, isPinned: true });
        isPinnedRef.current = true;
    }, [shiftTooltip?.shiftId, schedule, shiftConstraintBreaches]);

    useEffect(() => {
        if (!shiftTooltip?.isPinned) {
            return;
        }

        const handleGlobalClick = () => {
            if (justClickedShiftRef.current) {
                justClickedShiftRef.current = false;
                return;
            }
            setShiftTooltip(null);
            isPinnedRef.current = false;
        };

        document.addEventListener('click', handleGlobalClick);
        return () => {
            document.removeEventListener('click', handleGlobalClick);
        };
    }, [shiftTooltip?.isPinned]);

    return {
        shiftTooltip,
        containerRef,
        handleShiftMouseEnter,
        handleShiftMouseLeave,
        handleShiftClick,
        handleGenericMouseEnter,
    };
};
